<template>
  <layouts-auth>
    <template v-if="store.state.video.videoDetails">
      <div v-if="store.state.video.videoDetails.embed_url" class="bg-neutral-900 text-white">
        <layouts-auth-width>
          <div class="sm:pt-7">
            <template v-if="store.state.video.videoDetails.role">
              <div class="font-light text-base sm:text-xl px-2 sm:px-0 py-1">{{ store.state.video.videoDetails.role.name }}</div>
            </template>
            <div class="aspect-w-16 aspect-h-9">
              <iframe :src="embedSrc" width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen; picture-in-picture"></iframe>
            </div>
            <div class="px-2 sm:px-0 py-4">
              <div class="flex items-center justify-between">
                <div class="font-semibold">
                  <div class="text-base sm:text-lg">{{ store.state.video.videoDetails.title }}</div>
                </div>
                <div class="flex items-center justify-center">
                  <video-details-completion-button :isCompleted="isCompleted" class="mr-2" />
                  <video-details-bookmark-button :isBookmarked="isBookmarked" />
                </div>
              </div>
            </div>
          </div>
        </layouts-auth-width>
      </div>
      <div class="bg-gray-100 mb-6">
        <layouts-auth-width>
          <div class="px-2 sm:px-0 py-4 md:py-8">
            <div class="v-html whitespace-pre-wrap" v-html="store.state.video.videoDetails.body"></div>
          </div>
        </layouts-auth-width>
      </div>
      <div class="mb-6">
        <layouts-auth-width>
          <video-details-category :categories="store.state.video.videoDetails.video_categories" />
        </layouts-auth-width>
      </div>
    </template>
    <template v-else>
      <layouts-auth-width>
        <div class="padding-auth-contents">
          動画が見つかりませんでした。
        </div>
      </layouts-auth-width>
    </template>
  </layouts-auth>
</template>

<script lang="ts">
import { defineComponent, onMounted, onBeforeUpdate, computed } from 'vue'
import { useStore } from '@/store/index'
import { useRoute } from 'vue-router'
import { axios, ApiResponse } from '@/plugins/axios'
import { VideoAttributes } from '@/types/models/video'
import { dayjs } from '@/plugins/dayjs'

import LayoutsAuth from '@/layouts/Auth.vue'
import LayoutsAuthWidth from '@/layouts/AuthWidth.vue'
import VideoDetailsBookmarkButton from '@/components/VideoDetailsBookmarkButton.vue'
import VideoDetailsCompletionButton from '@/components/VideoDetailsCompletionButton.vue'
import VideoDetailsCategory from '@/components/VideoDetailsCategory.vue'

export default defineComponent({
  components: {
    LayoutsAuth,
    LayoutsAuthWidth,
    VideoDetailsBookmarkButton,
    VideoDetailsCompletionButton,
    VideoDetailsCategory
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const isCompleted = computed(
      () => !!(Array.isArray(store.state.video.videoDetails?.video_completions) && store.state.video.videoDetails?.video_completions.length)
    )
    const isBookmarked = computed(
      () => !!(Array.isArray(store.state.video.videoDetails?.video_bookmarks) && store.state.video.videoDetails?.video_bookmarks.length)
    )

    const initialize = () => {
      // API は変更しない -> /video/:id のみ呼ぶ
      const getVideo = axios.get<ApiResponse<VideoAttributes>>(`/video/${route.params.video_id}`)
      const browsing = axios.post(`/video/browsing/${route.params.video_id}`)
      Promise.allSettled([getVideo, browsing]).then((resolve) => {
        const videoDetails = resolve[0].status === 'fulfilled' ? resolve[0].value.data.payload : undefined
        store.commit('video/setVideoDetails', videoDetails)
      })
    }

    onBeforeUpdate(() => {
      initialize()
    })

    onMounted(() => {
      initialize()
    })

    /**
     * 再生開始秒の決定ロジック（優先順位）:
     * 1) ルート params: start_seconds
     * 2) API の videoDetails.start_time
     * 3) クエリ: ?start= / ?start_seconds=
     * ただし 0 以下や NaN の場合は無視
     */
    const resolveStartSeconds = (): number | null => {
      const vd: any = store.state.video.videoDetails
      // route.params は string | string[] の可能性があるので安全に取得
      const pStart = (route.params as any)?.start_seconds
      const qStart = route.query?.start
      const qStart2 = route.query?.start_seconds

      const tryNum = (v: any): number | null => {
        if (v === undefined || v === null) return null
        const n = Number(v)
        if (Number.isNaN(n) || n < 1) return null // 0 は付与しない（未指定扱い）
        return Math.floor(n)
      }

      return (
        tryNum(pStart) ??
        tryNum(vd?.start_time) ?? // ← バックエンドで保存した start_time を最優先の次に使用
        tryNum(qStart) ??
        tryNum(qStart2) ??
        null
      )
    }

    /**
     * 埋め込み URL に開始秒を付与（YouTube: ?start=、Vimeo: #t=Ns）
     * 既に start/t パラメータや #t= が含まれている場合はそのまま返す
     */
    const embedSrc = computed(() => {
      const vd = store.state.video.videoDetails
      if (!vd || !vd.embed_url) return ''
      let url: string = vd.embed_url

      // すでに開始指定が含まれているなら追記しない
      if (/[?&#](start|t)=/.test(url) || /#t=/.test(url)) {
        return url
      }

      const startNum = resolveStartSeconds()
      if (startNum === null) return url

      const isVimeo = /vimeo\.com|player\.vimeo\.com/.test(url)
      if (isVimeo) {
        // Vimeo はフラグメントで指定 (#t=90s)
        // 既にフラグメントがある場合は &t= を後置、ない場合は #t= で開始秒を付与
        url = url.includes('#') ? `${url}&t=${startNum}s` : `${url}#t=${startNum}s`
        return url
      }

      // YouTube embed 等は query に start= を付与
      const sep = url.includes('?') ? '&' : '?'
      url = `${url}${sep}start=${startNum}`
      return url
    })

    return {
      dayjs,
      store,
      isCompleted,
      isBookmarked,
      embedSrc
    }
  }
})
</script>
